// Global error handling utilities for frontend
import { AxiosError } from 'axios';

export interface AppError {
  code: string;
  message: string;
  details?: any;
  timestamp?: string;
  requestId?: string;
}

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

// Error types enum
export enum ErrorTypes {
  // Network errors
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT_ERROR',
  
  // Authentication errors
  AUTH_INVALID_TOKEN = 'AUTH_INVALID_TOKEN',
  AUTH_EXPIRED_TOKEN = 'AUTH_EXPIRED_TOKEN',
  AUTH_INSUFFICIENT_PERMISSIONS = 'AUTH_INSUFFICIENT_PERMISSIONS',
  
  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  
  // Business logic errors
  RESOURCE_NOT_FOUND = 'RESOURCE_NOT_FOUND',
  DUPLICATE_RESOURCE = 'DUPLICATE_RESOURCE',
  
  // System errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// User-friendly error messages
const ERROR_MESSAGES: Record<string, string> = {
  // Network errors
  NETWORK_ERROR: 'ネットワークエラーが発生しました。インターネット接続を確認してください。',
  TIMEOUT_ERROR: 'リクエストがタイムアウトしました。しばらく待ってから再試行してください。',
  
  // Authentication errors
  AUTH_INVALID_TOKEN: 'ログインセッションが無効です。再度ログインしてください。',
  AUTH_EXPIRED_TOKEN: 'ログインセッションが期限切れです。再度ログインしてください。',
  AUTH_INSUFFICIENT_PERMISSIONS: 'この操作を実行する権限がありません。',
  AUTH_MISSING_TOKEN: 'ログインが必要です。',
  
  // Validation errors
  VALIDATION_REQUIRED_FIELD: '必須項目が入力されていません。',
  VALIDATION_INVALID_FORMAT: '入力形式が正しくありません。',
  VALIDATION_DUPLICATE_VALUE: '既に存在する値です。',
  VALIDATION_INVALID_VALUE: '無効な値です。',
  
  // Business logic errors
  TEAM_NOT_FOUND: 'チームが見つかりません。',
  MEMBER_NOT_FOUND: 'メンバーが見つかりません。',
  SCHEDULE_NOT_FOUND: 'スケジュールが見つかりません。',
  EQUIPMENT_NOT_FOUND: '用具が見つかりません。',
  INVALID_INVITE_CODE: '招待コードが無効です。',
  
  // External service errors
  LINE_API_ERROR: 'LINE連携でエラーが発生しました。',
  LINE_AUTH_ERROR: 'LINE認証に失敗しました。',
  
  // System errors
  INTERNAL_ERROR: 'システムエラーが発生しました。しばらく待ってから再試行してください。',
  DATABASE_ERROR: 'データベースエラーが発生しました。',
  UNKNOWN_ERROR: '予期しないエラーが発生しました。',
};

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// Get error severity based on error code
export const getErrorSeverity = (code: string): ErrorSeverity => {
  if (code && code.startsWith('AUTH_')) {
    return ErrorSeverity.HIGH;
  }
  
  if (code && (code.includes('NETWORK') || code.includes('TIMEOUT'))) {
    return ErrorSeverity.MEDIUM;
  }
  
  if (code && code.includes('VALIDATION')) {
    return ErrorSeverity.LOW;
  }
  
  if (code && (code.includes('INTERNAL') || code.includes('DATABASE'))) {
    return ErrorSeverity.CRITICAL;
  }
  
  return ErrorSeverity.MEDIUM;
};

// Parse error from different sources
export const parseError = (error: any): AppError => {
  // Handle Axios errors
  if (error.isAxiosError || error.response) {
    const axiosError = error as AxiosError;
    
    if (axiosError.response?.data) {
      const responseData = axiosError.response.data as any;
      
      if (responseData.error) {
        return {
          code: responseData.error.code || ErrorTypes.UNKNOWN_ERROR,
          message: responseData.error.message || ERROR_MESSAGES[responseData.error.code] || 'エラーが発生しました',
          details: responseData.error.details,
          timestamp: responseData.timestamp,
          requestId: responseData.requestId,
        };
      }
    }
    
    // Handle network errors
    if (axiosError.code === 'NETWORK_ERROR' || !axiosError.response) {
      return {
        code: ErrorTypes.NETWORK_ERROR,
        message: ERROR_MESSAGES[ErrorTypes.NETWORK_ERROR],
      };
    }
    
    // Handle timeout errors
    if (axiosError.code === 'ECONNABORTED') {
      return {
        code: ErrorTypes.TIMEOUT_ERROR,
        message: ERROR_MESSAGES[ErrorTypes.TIMEOUT_ERROR],
      };
    }
    
    // Handle HTTP status codes
    const status = axiosError.response?.status;
    if (status === 401) {
      return {
        code: ErrorTypes.AUTH_EXPIRED_TOKEN,
        message: ERROR_MESSAGES[ErrorTypes.AUTH_EXPIRED_TOKEN],
      };
    }
    
    if (status === 403) {
      return {
        code: ErrorTypes.AUTH_INSUFFICIENT_PERMISSIONS,
        message: ERROR_MESSAGES[ErrorTypes.AUTH_INSUFFICIENT_PERMISSIONS],
      };
    }
    
    if (status === 404) {
      return {
        code: ErrorTypes.RESOURCE_NOT_FOUND,
        message: 'リソースが見つかりません',
      };
    }
    
    if (status && status >= 500) {
      return {
        code: ErrorTypes.INTERNAL_ERROR,
        message: ERROR_MESSAGES[ErrorTypes.INTERNAL_ERROR],
      };
    }
  }
  
  // Handle JavaScript errors
  if (error instanceof Error) {
    return {
      code: ErrorTypes.UNKNOWN_ERROR,
      message: error.message || ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR],
    };
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return {
      code: ErrorTypes.UNKNOWN_ERROR,
      message: error,
    };
  }
  
  // Handle custom app errors
  if (error.code && error.message) {
    return {
      code: error.code,
      message: ERROR_MESSAGES[error.code] || error.message,
      details: error.details,
      timestamp: error.timestamp,
      requestId: error.requestId,
    };
  }
  
  // Default unknown error
  return {
    code: ErrorTypes.UNKNOWN_ERROR,
    message: ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR],
  };
};

// Get user-friendly error message
export const getUserFriendlyMessage = (error: AppError): string => {
  // Check if we have a custom message for this error code
  if (ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Return the original message if no custom message is available
  return error.message || ERROR_MESSAGES[ErrorTypes.UNKNOWN_ERROR];
};

// Check if error should trigger logout
export const shouldTriggerLogout = (error: AppError): boolean => {
  return error.code === ErrorTypes.AUTH_EXPIRED_TOKEN || 
         error.code === ErrorTypes.AUTH_INVALID_TOKEN;
};

// Check if error should be retried
export const shouldRetry = (error: AppError): boolean => {
  return error.code === ErrorTypes.NETWORK_ERROR || 
         error.code === ErrorTypes.TIMEOUT_ERROR ||
         error.code === ErrorTypes.INTERNAL_ERROR;
};

// Log error for debugging (in development) or analytics (in production)
export const logError = (error: AppError, context?: Record<string, any>) => {
  const logData = {
    timestamp: new Date().toISOString(),
    error: {
      code: error.code,
      message: error.message,
      details: error.details,
      requestId: error.requestId,
    },
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
  
  if (import.meta.env.DEV) {
    console.error('Application Error:', logData);
  } else {
    // In production, you might want to send this to an error tracking service
    // like Sentry, LogRocket, or custom analytics
    console.error('Error:', error.code, error.message);
  }
};

// Error boundary helper
export const handleGlobalError = (error: any, errorInfo?: any) => {
  const appError = parseError(error);
  logError(appError, { errorInfo });
  
  // You can add additional global error handling logic here
  // such as showing a global error toast or redirecting to an error page
};

// Validation error helpers
export const formatValidationErrors = (validationErrors: ValidationError[]): string => {
  if (validationErrors.length === 1) {
    return validationErrors[0].message;
  }
  
  return validationErrors.map(error => `${error.field}: ${error.message}`).join('\n');
};

export const getFieldError = (validationErrors: ValidationError[], fieldName: string): string | undefined => {
  const fieldError = validationErrors.find(error => error.field === fieldName);
  return fieldError?.message;
};